import { Input } from "../ui/input";
import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Card, CardHeader, CardContent } from "../ui/card";
import { User } from "lucide-react";
import logo from "../../logo.png";
import humanimg from "../../humanimg.png";
import { useNavigate } from "react-router-dom";
import AIIcon from "../../aichat.webp";

function AIChat() {
  const steps = [
    "pcpName",
    "pcpPhone",
    "diagnoses",
    "otherDiagnosis",
    "allergies",
    "allergyList",
    "medications",
    "glpUsage",
    "glpDetails",
    "glpSideEffects",
    "sideEffectsList",
    "summary",
  ];

  const diagnosisOptions = [
    "Type 2 Diabetes",
    "Obesity/Overweight",
    "Cardiovascular Disease",
    "Hypertension",
    "Hyperlipidemia (high cholesterol)",
    "Kidney Disease",
    "Gastrointestinal Disorders (e.g., IBS)",
    "Thyroid Disorders",
    "History of Pancreatitis",
    "History of Gallbladder Disease",
    "Other",
  ];

  const navigate = useNavigate();

  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState({
    pcpName: "",
    pcpPhone: "",
    diagnoses: [],
    otherDiagnosis: "",
    allergies: "",
    allergyList: "",
    medications: [],
    glpUsage: "",
    glpDetails: "",
    glpSideEffects: "",
    sideEffectsList: "",
  });

  const [tempMedication, setTempMedication] = useState({
    name: "",
    dosage: "",
    frequency: "",
  });

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const chatEndRef = useRef(null);

  const currentStep = steps[stepIndex] || null;

  // Initialize with greeting and first question once on mount
  useEffect(() => {
    setMessages([
      { from: "assistant", text: "👋 Hi! Let’s start your medical intake." },
      { from: "assistant", text: getQuestionText(steps[0]) },
    ]);
  }, []);

  // On stepIndex update (except first and summary), add current question if not duplicate
  useEffect(() => {
    if (!currentStep || currentStep === "summary" || stepIndex === 0) return;
    const question = getQuestionText(currentStep);
    const lastAssistantText = messages
      .slice()
      .reverse()
      .find((msg) => msg.from === "assistant")?.text;
    if (lastAssistantText !== question) {
      addAssistantMessage(question);
    }
    // eslint-disable-next-line
  }, [stepIndex]);

  // Scroll to bottom on messages update
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  function addAssistantMessage(text) {
    setMessages((prev) => [...prev, { from: "assistant", text }]);
  }

  function addUserMessage(text) {
    setMessages((prev) => [...prev, { from: "user", text }]);
  }

  function getQuestionText(step) {
    switch (step) {
      case "pcpName":
        return "What is your Primary Care Physician (PCP) Name?";
      case "pcpPhone":
        return "What is your PCP Phone Number?";
      case "diagnoses":
        return "Do you have any of the following current diagnoses? (Select all that apply)";
      case "otherDiagnosis":
        return "Please specify your other diagnosis:";
      case "allergies":
        return "Do you have any allergies (medications, food, environmental)?";
      case "allergyList":
        return "Please list your allergies:";
      case "medications":
        return "Please list your current medications one by one (include dosage and frequency).";
      case "glpUsage":
        return "Have you used any GLP-1 medications previously?";
      case "glpDetails":
        return "Which GLP-1 medication(s) and when?";
      case "glpSideEffects":
        return "Did you experience any side effects?";
      case "sideEffectsList":
        return "Please specify the side effects:";
      case "summary":
        return "Here’s a summary of your information. Is this correct?";
      default:
        return "";
    }
  }

  const confirmAnswer = (answer) => {
    addUserMessage(answer.toString());

    let updatedFormData = { ...formData };
    if (currentStep === "diagnoses") {
      updatedFormData.diagnoses = selectedOptions;
    } else {
      updatedFormData[currentStep] = answer;
    }
    setFormData(updatedFormData);
    setInputValue("");
    setSelectedOptions([]);
    advanceStep(stepIndex + 1, updatedFormData);
  };

  function advanceStep(newIndex, data) {
    if (newIndex >= steps.length) {
      setStepIndex(steps.length);
      return;
    }
    let nextStep = steps[newIndex];
    if (nextStep === "otherDiagnosis" && !data.diagnoses.includes("Other")) {
      newIndex++;
    }
    if (nextStep === "allergyList" && data.allergies !== "Yes") {
      newIndex++;
    }
    if (
      (nextStep === "glpDetails" || nextStep === "glpSideEffects") &&
      data.glpUsage !== "Yes"
    ) {
      newIndex++;
    }
    if (nextStep === "sideEffectsList" && data.glpSideEffects !== "Yes") {
      newIndex++;
    }
    setStepIndex(newIndex);
  }

  function addMedication() {
    if (
      tempMedication.name.trim() &&
      tempMedication.dosage.trim() &&
      tempMedication.frequency.trim()
    ) {
      const updatedMedications = [...formData.medications, tempMedication];
      setFormData({ ...formData, medications: updatedMedications });
      addUserMessage(
        `+ Medication: ${tempMedication.name} - ${tempMedication.dosage} - ${tempMedication.frequency}`
      );
      setTempMedication({ name: "", dosage: "", frequency: "" });
    }
  }

  // Send message with Enter key fix: wrap input in form and handle submit
  function handleFormSubmit(e) {
    e.preventDefault();
    if (inputValue.trim()) {
      confirmAnswer(inputValue.trim());
    }
  }

  const renderInput = () => {
    if (!currentStep || currentStep === "summary") return null;

    switch (currentStep) {
      case "pcpName":
      case "pcpPhone":
      case "otherDiagnosis":
      case "allergyList":
      case "glpDetails":
      case "sideEffectsList":
        return (
          <form className="flex gap-2 mt-4" onSubmit={handleFormSubmit}>
            <Input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your answer..."
              className="w-full mb-4 px-4 py-2 border border-gray-300 rounded-md focus:outline-none"
            />
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-semibold disabled:opacity-50 transition-colors duration-200"
              disabled={!inputValue.trim()}
              type="submit"
            >
              Confirm
            </Button>
          </form>
        );
      case "diagnoses":
        return (
          <div className="space-y-2 mt-4">
            <div className="max-h-40 overflow-y-auto border rounded-md p-3 scrollbar-thin scrollbar-thumb-blue-500">
              {diagnosisOptions.map((option) => (
                <label
                  key={option}
                  className="text-sm block cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    value={option}
                    className="mr-2"
                    checked={selectedOptions.includes(option)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedOptions([...selectedOptions, option]);
                      } else {
                        setSelectedOptions(
                          selectedOptions.filter((o) => o !== option)
                        );
                      }
                    }}
                  />
                  {option}
                </label>
              ))}
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md w-full font-semibold disabled:opacity-50 transition-colors duration-200"
              disabled={selectedOptions.length === 0}
              onClick={() => confirmAnswer(selectedOptions)}
            >
              Confirm
            </Button>
          </div>
        );
      case "allergies":
      case "glpUsage":
      case "glpSideEffects":
        return (
          <div className="flex gap-4 mt-4">
            {["Yes", "No"].map((opt) => (
              <Button
                key={opt}
                onClick={() => confirmAnswer(opt)}
                className={`flex-1 px-4 py-2 rounded-md text-white font-semibold transition-colors duration-200 ${
                  opt === "Yes"
                    ? "bg-green-500 hover:bg-green-600"
                    : "bg-red-500 hover:bg-red-600"
                }`}
              >
                {opt}
              </Button>
            ))}
          </div>
        );
      case "medications":
        return (
          <div className="mt-4">
            <div className="flex gap-2 mb-2">
              <Input
                type="text"
                placeholder="Name"
                value={tempMedication.name}
                onChange={(e) =>
                  setTempMedication({ ...tempMedication, name: e.target.value })
                }
                className="border p-2 rounded-md w-1/3 focus:ring-2 focus:ring-blue-400"
              />
              <Input
                type="text"
                placeholder="Dosage"
                value={tempMedication.dosage}
                onChange={(e) =>
                  setTempMedication({
                    ...tempMedication,
                    dosage: e.target.value,
                  })
                }
                className="border p-2 rounded-md w-1/3 focus:ring-2 focus:ring-blue-400"
              />
              <Input
                type="text"
                placeholder="Frequency"
                value={tempMedication.frequency}
                onChange={(e) =>
                  setTempMedication({
                    ...tempMedication,
                    frequency: e.target.value,
                  })
                }
                className="border p-2 rounded-md w-1/3 focus:ring-2 focus:ring-blue-400"
              />
              <Button
                onClick={addMedication}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-semibold transition-colors duration-200"
              >
                Add Medication
              </Button>
            </div>

            {formData.medications.length > 0 && (
              <ul className="list-disc pl-6 text-sm mb-2">
                {formData.medications.map((m, idx) => (
                  <li
                    key={idx}
                  >{`${m.name} - ${m.dosage} - ${m.frequency}`}</li>
                ))}
              </ul>
            )}

            <Button
              onClick={() => confirmAnswer("Medications entered")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md w-full font-semibold transition-colors duration-200 mt-2"
            >
              Next
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  if (currentStep === "summary") {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <header className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="text-2xl font-bold tracking-wide flex items-center">
              <img src={logo} alt="Logo" className="w-[200px] h-auto mx-auto" />
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto my-12 p-6 bg-white rounded shadow">
          <h2 className="text-2xl font-bold mb-6">
            Summary of Your Information
          </h2>
          {steps
            .filter((step) => step !== "summary")
            .map((step, idx) => {
              let answer = formData[step];
              if (Array.isArray(answer)) answer = answer.join(", ");
              if (!answer) answer = "-";
              const question = getQuestionText(step);
              return (
                <div key={idx} className="mb-4">
                  <p className="font-semibold">{question}</p>
                  <p className="ml-4">{answer}</p>
                </div>
              );
            })}

          <div className="flex gap-4 mt-8">
            <Button
              onClick={() => navigate("/products")}
              className="flex-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md font-semibold"
            >
              Yes, Submit
            </Button>
            <Button
              onClick={() => {
                setStepIndex(0);
                setFormData({
                  pcpName: "",
                  pcpPhone: "",
                  diagnoses: [],
                  otherDiagnosis: "",
                  allergies: "",
                  allergyList: "",
                  medications: [],
                  glpUsage: "",
                  glpDetails: "",
                  glpSideEffects: "",
                  sideEffectsList: "",
                });
                setMessages([
                  {
                    from: "assistant",
                    text: "👋 Hi! Let’s start your medical intake.",
                  },
                  {
                    from: "assistant",
                    text: getQuestionText(steps[0]),
                  },
                ]);
              }}
              className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-md font-semibold"
            >
              No, Restart
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold tracking-wide flex items-center">
            <img src={logo} alt="Logo" className="w-[200px] h-auto mx-auto" />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto mt-8 bg-white h-[530px] flex flex-col rounded shadow-lg overflow-hidden">
        <Card className="flex flex-col flex-1 overflow-hidden">
          <CardHeader className="custom-bg from-xaxathrive-primary to-blue-600 text-white !p-2 flex items-center gap-2">
            <Avatar className="bg-white bg-opacity-20 w-8 h-8">
              <AvatarFallback className="bg-slate-100 text-slate-600">
                <img src={humanimg} className="w-6 h-6" alt="User avatar" />
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="font-semibold text-slate-600 text-sm">
                AI Doctor Assistant - Ticket Id: 12457896
              </h2>
              <p className="text-xs text-slate-600">
                Here to help you to place your best
              </p>
            </div>
            <div className="ml-auto">
              <span className="inline-flex items-center gap-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                <div className="w-2 h-2 bg-white rounded-full"></div>
                Online
              </span>
            </div>
          </CardHeader>

          <CardContent className="flex flex-col flex-1 p-6 overflow-hidden">
            <div className="flex-1 overflow-y-auto pr-3 mb-4 space-y-4 scrollbar-thin scrollbar-thumb-blue-500">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex items-start ${
                    msg.from === "assistant" ? "justify-start" : "justify-end"
                  }`}
                >
                  {msg.from === "assistant" && (
                    <Avatar className="bg-slate-300 mr-2">
                      <AvatarFallback className="bg-slate-300 text-slate-600">
                        <img
                          src={AIIcon}
                          alt="Assistant icon"
                          className="w-6 h-6"
                        />
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div
                    className={`max-w-[75%] py-2 px-4 rounded-md shadow flex items-center gap-2 ${
                      msg.from === "assistant"
                        ? "text-sm bg-primaryColor border-primaryColor border text-gray-900"
                        : "text-sm bg-infoColor border border-infoColor"
                    }`}
                  >
                    {msg.text}
                  </div>
                  {msg.from === "user" && (
                    <Avatar className="bg-slate-300 ml-2">
                      <AvatarFallback className="bg-slate-300 text-slate-600">
                        <User size={16} />
                      </AvatarFallback>
                    </Avatar>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="border-t pt-4">{renderInput()}</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AIChat;
